import { useNavigate } from "react-router";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { sectionColorHex } from "../risikoanalyseStep/sectionColor";
import { bereichForRiskType, risikoobjektLabel } from "./risikoobjektLabel";
import type { RiskObjectLite } from "./risikoobjektLabel";

const BEREICH_COLOR_KEY = (b: 1 | 2 | 3 | 4) =>
  b === 1
    ? ("pillarOrange" as const)
    : b === 2
    ? ("blue" as const)
    : b === 3
    ? ("green" as const)
    : ("black" as const);

const BEREICH_NAME: Record<1 | 2 | 3 | 4, string> = {
  1: "Sachwerte",
  2: "Haftung",
  3: "Menschen",
  4: "Liquidität",
};

const useStyles = makeStyles({
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderRadius("10px"),
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    flexWrap: "wrap",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
  pillBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontWeight: 700,
    fontSize: "0.72rem",
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 2rem)",
    fontWeight: 400,
    color: BRAND.gold,
    flexGrow: 1,
  },
  links: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "12px",
    rowGap: "8px",
  },
  linkCard: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "rgba(255,255,255,0.08)",
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    textAlign: "left",
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.18)"),
    ...shorthands.borderRadius("8px"),
    transitionProperty: "background-color, border-color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.16)",
      ...shorthands.borderColor(BRAND.gold),
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },
  linkLabel: {
    fontSize: "0.7rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.6)",
    marginBottom: "2px",
    display: "block",
  },
  linkValue: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
  },
});

type Props = {
  risk: RiskObjectLite & {
    contactID?: number;
    contractID?: number | null;
    contract?: { versicherungsnummer?: string } | null;
    contactDisplayName?: string;
  };
};

const RiskDetailHeader = ({ risk }: Props) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const b = bereichForRiskType(risk.riskType);
  const accent = sectionColorHex[BEREICH_COLOR_KEY(b)];

  return (
    <div className={styles.bar}>
      <div className={styles.titleRow}>
        <span className={styles.pill} style={{ backgroundColor: accent }}>
          <span className={styles.pillBadge}>{b}</span>
          {BEREICH_NAME[b]}
        </span>
        <h1 className={styles.title}>
          {risk.riskType ?? "Risiko"}: {risikoobjektLabel(risk)}
        </h1>
      </div>
      <div className={styles.goldLine} />
      <div className={styles.links}>
        <button
          type="button"
          className={styles.linkCard}
          disabled={!risk.contactID}
          onClick={() => risk.contactID && navigate(`/contacts/${risk.contactID}`)}
        >
          <span className={styles.linkLabel}>Kontakt</span>
          <span className={styles.linkValue}>
            {risk.contactDisplayName ?? (risk.contactID ? `#${risk.contactID}` : "—")}
          </span>
        </button>
        <button
          type="button"
          className={styles.linkCard}
          disabled={!risk.contractID}
          onClick={() => risk.contractID && navigate(`/contracts/${risk.contractID}`)}
        >
          <span className={styles.linkLabel}>Vertrag</span>
          <span className={styles.linkValue}>
            {risk.contract?.versicherungsnummer ??
              (risk.contractID ? `#${risk.contractID}` : "—")}
          </span>
        </button>
      </div>
    </div>
  );
};

export default RiskDetailHeader;
